import React, { useState } from "react"
import { Modal, Button, Tabs, Tab } from "react-bootstrap"

import FavouriteEditor from "../FavouriteEditor"
import FavouriteSharer from "../FavouriteSharer"
import FavouritesList from "./FavouritesList"
import { withFavouritesConfig } from "../../config/FavouriteConfigProvider"
import { getFormattedMessage } from "../../../components/shared_components_registration"
import { getCurrentTime, stringifyDate } from "../../utils"
import EditSingleFileModal from "../../administration/file_uploads/EditSingleFileModal"

const FavouritesManager = ({
  show,
  onHide,
  favouriteGraphSets = [],
  favouriteRunSets = [],
  deleteFavouriteRunSet,
  deleteFavouriteGraphSet,
  restoreFavouriteRunSet,
  restoreFavouriteGraphSet,
  updateFavouriteRunSet,
  updateFavouriteGraphSet,
  loadFavouriteGraphs,
  intl,
}) => {
  const getText = (id, msg, val = {}) => getFormattedMessage(id, msg, true, intl, val)
  const registration = {
    fave_graph_label: getText("favourites.favourite.graphs", "Favourite Graphs"),
    fave_run_label: getText("favourites.favourite.runs", "Favourite Runs"),
    edit_label: getText("general.edit", "Edit"),
  }
  const [editingType, setEditingType] = useState(null)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [selectedTab, setSelectedTab] = useState("graph") // We need to manage this so that tab is preserved when returning from editor
  const [showShareModal, setShowShareModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedFavorite, setSelectedFavorite] = useState(null)
  const selectTab = (tab) => setSelectedTab(tab)

  const onSelect = (type, template) => {
    setEditingType(type)
    setEditingTemplate(template)
  }

  const resetTypeTemplate = () => {
    setEditingType(null)
    setEditingTemplate(null)
  }

  const onExitEditor = () => resetTypeTemplate()

  const onDelete = () => {
    if (editingType === "run") {
      deleteFavouriteRunSet(editingTemplate.id)
    } else if (editingType === "graph") {
      deleteFavouriteGraphSet(editingTemplate.id)
    }

    resetTypeTemplate()
  }

  const onRestore = (interfaces) => {
    if (editingType === "run") {
      restoreFavouriteRunSet(editingTemplate.id)
    } else if (editingType === "graph") {
      restoreFavouriteGraphSet(editingTemplate.id, interfaces)
    }
    resetTypeTemplate()
  }

  const onUpdate = (name, description, interfaces) => {
    if (editingType === "run") {
      updateFavouriteRunSet(editingTemplate.id, name, editingTemplate.runs, description)
    } else if (editingType === "graph") {
      updateFavouriteGraphSet(
        editingTemplate.id,
        name,
        editingTemplate.graphConfigs,
        description,
        editingTemplate.view,
        interfaces,
      )
    }

    resetTypeTemplate()
  }

  const nameCheck = (name) => {
    let refSet
    if (editingType === "run") {
      refSet = favouriteRunSets
    } else if (editingType === "graph") {
      refSet = favouriteGraphSets
    }

    if (!editingTemplate || !refSet) return false

    return refSet.some((r) => r.name === name && r.id !== editingTemplate.id)
  }

  const onShareClick = (f) => {
    setSelectedFavorite(f)
    setShowShareModal(true)
  }

  const onExportClick = (f) => {
    // turn the run data into a string
    const data = JSON.stringify(f)
    const blob = new Blob([data], { type: "application/json" })
    const jsonObjectUrl = URL.createObjectURL(blob)

    const filename = f.name + " " + stringifyDate(getCurrentTime()) + ".json"
    const anchorEl = document.createElement("a")
    anchorEl.href = jsonObjectUrl
    anchorEl.download = filename
    anchorEl.click()
  }

  const onImportClick = () => {
    setShowUploadModal(true)
  }

  if (showShareModal && selectedFavorite) {
    return (
      <FavouriteSharer
        getText={getText}
        favourite={selectedFavorite}
        show={showShareModal}
        onHide={() => {
          setShowShareModal(false)
          setSelectedFavorite(null)
        }}
      />
    )
  } else if (editingType && editingTemplate) {
    let title
    if (editingType === "run") {
      title = `${registration.edit_label}: ${registration.fave_run_label}`
    } else if (editingType === "graph") {
      title = `${registration.edit_label}: ${registration.fave_graph_label}`
    }

    return (
      <FavouriteEditor
        getText={getText}
        title={title}
        initialName={editingTemplate.name}
        initialDescription={editingTemplate.description}
        graphInterfaces={editingTemplate.graph_interfaces}
        encoded={
          editingType === "graph" && editingTemplate.encoded ? JSON.parse(editingTemplate.encoded) : []
        }
        show={show}
        onHide={onExitEditor}
        onDelete={editingTemplate.trashed ? null : onDelete}
        onRestore={editingTemplate.trashed ? onRestore : null}
        onUpdate={onUpdate}
        nameCheck={nameCheck}
      />
    )
  } else {
    if (showUploadModal) {
      return (
        <EditSingleFileModal
          intl={intl}
          file_id={null}
          show={showUploadModal}
          tableReload={null}
          onHide={() => {
            setShowUploadModal(false)
            loadFavouriteGraphs()
            resetTypeTemplate()
          }}
          singleFileType={"Graph Favourite Export"}
        />
      )
    } else {
      return (
        <Modal
          size={"lg"}
          dialogClassName={"definitions-modal"}
          show={show}
          onHide={onHide}
          backdrop={"static"}
        >
          <Modal.Header closeButton>
            <Modal.Title>{getText("favourites.manage.favourites", "Manage Favourites")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs id={"favourites-manager-tabs"} activeKey={selectedTab} onSelect={selectTab}>
              {[
                ["graph", registration.fave_graph_label, favouriteGraphSets],
                ["run", registration.fave_run_label, favouriteRunSets],
              ].map((type, idx) => (
                <Tab key={`faveTab${idx}`} eventKey={type[0]} title={type[1]}>
                  <FavouritesList
                    type={type[0]}
                    getText={getText}
                    favourites={type[2]}
                    onSelect={onSelect}
                    onShareClick={onShareClick}
                    onExportClick={onExportClick}
                    intl={intl}
                  />
                </Tab>
              ))}
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            {selectedTab === "graph" && (
              <Button
                title={getText("general.import", "Import")}
                onClick={onImportClick}
                variant={"outline-primary"}
                style={{ marginRight: "auto" }}
              >
                <span className="fa fa-upload"></span>
              </Button>
            )}
            <Button onClick={onHide} variant={"outline-primary"}>
              {getText("general.closeButton", "Close")}
            </Button>
          </Modal.Footer>
        </Modal>
      )
    }
  }
}

export default withFavouritesConfig(FavouritesManager)
